import React, { useEffect, useState, useContext } from 'react';
import axios from 'axios';
import { getAuth } from 'firebase/auth';
import { NavLink } from 'react-router-dom';
import '../styles/Home.css';
import { FaThumbsUp, FaPlay, FaStar } from 'react-icons/fa';
import { AudioContext } from '../context/AudioContext';

const Home = () => {
  const { handleTrackClick } = useContext(AudioContext);
  const [uploads, setUploads] = useState([]);
  const [genreTracks, setGenreTracks] = useState([]);
  const [activeTab, setActiveTab] = useState('genres');
  const [loading, setLoading] = useState(true);
  const [plan, setPlan] = useState(null);
  const currentUser = getAuth().currentUser;

  // Fetch user plan
  useEffect(() => {
    const fetchUserPlan = async () => {
      try {
        if (currentUser) {
          const token = await currentUser.getIdToken();
          const response = await axios.get(
            `https://sync-api-troz.onrender.com/api/users/firebase/${currentUser.uid}`,
            { headers: { Authorization: `Bearer ${token}` } }
          );
          setPlan(response.data.plan);
        }
      } catch (error) {
        console.error('Error fetching user plan:', error);
      }
    };

    fetchUserPlan();
  }, [currentUser]);

  // Fetch uploads and genre tracks
  useEffect(() => {
    const fetchData = async () => {
      setLoading(true);
      try {
        if (currentUser) {
          const token = await currentUser.getIdToken();

          // Fetch matched uploads
          const uploadsResponse = await axios.get(
            'https://sync-api-troz.onrender.com/api/uploads/matched-uploads',
            {
              params: { firebaseUid: currentUser.uid },
              headers: { Authorization: `Bearer ${token}` },
            }
          );
          setUploads(uploadsResponse.data);
          console.log('Matched Uploads:', uploadsResponse.data); // Debug

          // Fetch genre tracks
          const genreResponse = await axios.get(
            'https://sync-api-troz.onrender.com/api/uploads/tracks/genres',
            {
              params: { firebaseUid: currentUser.uid },
              headers: { Authorization: `Bearer ${token}` },
            }
          );
          setGenreTracks(genreResponse.data);
          console.log('Genre Tracks:', genreResponse.data); // Debug
        }
      } catch (error) {
        console.error('Error fetching data:', error);
      } finally {
        setLoading(false);
      }
    };

    if (currentUser) {
      fetchData();
    } else {
      setLoading(false);
    }
  }, [currentUser]);

  // Handle liking a track
  const handleLikeTrack = async (trackId, trackUserId) => {
    if (!currentUser) {
      alert('You must be logged in to like a track.');
      return;
    }

    if (trackUserId === currentUser.uid) {
      alert("You can't like your own track.");
      return;
    }

    try {
      const token = await currentUser.getIdToken();
      await axios.post(
        'https://sync-api-troz.onrender.com/api/uploads/like',
        {
          trackId,
          firebaseUid: currentUser.uid,
        },
        {
          headers: { Authorization: `Bearer ${token}` },
        }
      );

      const updateLikes = (tracks) =>
        tracks.map((track) =>
          track._id === trackId
            ? {
                ...track,
                likes: (track.likes || 0) + 1,
                likedBy: [...(track.likedBy || []), currentUser.uid],
              }
            : track
        );

      if (activeTab === 'stars') {
        setUploads((prevUploads) => updateLikes(prevUploads));
      } else {
        setGenreTracks((prevTracks) => updateLikes(prevTracks));
      }

      alert('Track liked successfully!');
    } catch (error) {
      console.error('Error liking track:', error.response?.data || error.message);
      if (error.response?.data?.message === 'You have already liked this track') {
        alert('You have already liked this track.');
      } else {
        alert('An error occurred while liking the track.');
      }
    }
  };

  // Handle starring a track
  const handleStarTrack = async (trackId) => {
    if (!currentUser) {
      alert('You must be logged in to star a track.');
      return;
    }

    const track = genreTracks.find((t) => t._id === trackId) || uploads.find((t) => t._id === trackId);
    if (track?.user?.firebaseUid === currentUser.uid) {
      alert("You can't star your own track.");
      return;
    }

    try {
      const token = await currentUser.getIdToken();
      await axios.post(
        'https://sync-api-troz.onrender.com/api/uploads/star',
        {
          trackId,
          userId: currentUser.uid,
        },
        {
          headers: { Authorization: `Bearer ${token}` },
        }
      );

      const updateStars = (tracks) =>
        tracks.map((track) =>
          track._id === trackId
            ? {
                ...track,
                stars: (track.stars || 0) + 1,
                starredBy: [...(track.starredBy || []), currentUser.uid],
              }
            : track
        );

      // Only update genreTracks to match mobile app
      setGenreTracks((prevTracks) => updateStars(prevTracks));

      alert('Track starred successfully!');
    } catch (error) {
      console.error('Error starring track:', error.response?.data || error.message);
      if (error.response?.data?.message === 'You have already starred a track from this artist') {
        alert('You already starred a track from this user.');
      } else {
        alert('An error occurred while starring the track.');
      }
    }
  };

  if (loading) {
    return (
      <div className="loading-container">
        <div className="spinner"></div>
        <p>Loading...</p>
      </div>
    );
  }

  return (
    <div className={`home-container ${activeTab === 'stars' ? 'stars-tab' : 'genres-tab'}`}>
      <header className="tab-container-home">
        <div className={`tab-sub-container ${activeTab === 'genres' ? 'active-tab-container' : ''}`}>
          <button
            className={`tab-button ${activeTab === 'genres' ? 'active-tab' : ''}`}
            onClick={() => setActiveTab('genres')}
          >
            Your Genres
          </button>
        </div>
        <div className={`tab-sub-container ${activeTab === 'stars' ? 'active-tab-container' : ''}`}>
          <button
            className={`tab-button ${activeTab === 'stars' ? 'active-tab' : ''}`}
            onClick={() => setActiveTab('stars')}
          >
            Uploads from Stars
          </button>
        </div>
      </header>

      <div className="main-content-home">
        <div className="music-feed">
          {activeTab === 'stars' ? (
            uploads.length > 0 ? (
              uploads.map((upload) => (
                <div
                  key={upload._id}
                  className="upload-item"
                  onClick={() => handleTrackClick(upload)}
                >
                  <div className="upload-item-container">
                    <img src={upload.coverPhoto} alt={upload.title} className="upload-cover" />
                    <div className="upload-details">
                      <h3>{upload.title}</h3>
                      <p className="home-artist-upload">
                        {upload.genre} • {upload.user.artistName || 'Unknown Artist'}
                      </p>
                      {/* <div className="likes-plays-section">
                        <span className="plays-count">
                          <FaPlay style={{ marginRight: '5px' }} /> {upload.plays || 0}
                        </span>
                        <span
                          className={`likes-count ${
                            upload.likedBy && upload.likedBy.includes(currentUser.uid) ? 'liked' : ''
                          }`}
                          onClick={(e) => {
                            e.stopPropagation();
                            handleLikeTrack(upload._id, upload.user._id);
                          }}
                        >
                          <FaThumbsUp style={{ marginRight: '5px' }} /> {upload.likes || 0}
                        </span>
                      </div> */}
                    </div>
                  </div>
                </div>
              ))
            ) : (
              <div className="empty-home-message">
                <h2>Collaborators' Uploads</h2>
                <p>
                  No matched artists' uploads available. Go to the search tab and send out stars!
                </p>
                <NavLink className="empty-home-go-to-search-btn" to="/search">
                  Search
                </NavLink>
              </div>
            )
          ) : genreTracks.length > 0 ? (
            genreTracks.map((track) => (
              <div
                key={track._id}
                className="upload-item"
                onClick={() => handleTrackClick(track)}
              >
                <div className="upload-item-container">
                  <img src={track.coverPhoto} alt={track.title} className="upload-cover" />
                  <div className="upload-details">
                    <h3>{track.title}</h3>
                    <p className="home-artist-upload">
                      {track.genre} • {track.user.artistName || 'Unknown Artist'}
                    </p>
                  </div>
                </div>
              </div>
            ))
          ) : (
            <div className="empty-home-message">
              <h2>Your Genres</h2>
              <p>No tracks available in your selected genres. Explore more genres!</p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default Home;